<template>
  <v-bottom-sheet
    ref="sheet"
    v-model="showBanner"
    persistent
    hide-overlay
    scrollable
    :retain-focus="false"
  >
    <div class="cookie-banner">
      <div class="container">
        <h3>{{ $t('cookieModal.headline') }}</h3>
        <p class="mt-4">
          <i18n path="cookieModal.info">
            <a
              v-if="customDataPrivacyUrl"
              :href="customDataPrivacyUrl"
              target="_blank"
            >
              {{ $t('jobalert.privacyPolicyLabel') }}
            </a>

            <router-link v-else :to="{ name: 'privacyPolicy' }">
              {{ $t('jobalert.privacyPolicyLabel') }}
            </router-link>
          </i18n>
        </p>

        <div class="mt-4">
          <v-row dense>
            <v-col dense cols="12" sm="4" md="3" xl="2" class="d-flex">
              <v-switch
                v-model="allowRequired"
                disabled
                hide-details
                :label="$t('cookieModal.requiredCookies')"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="4" md="3" xl="2">
              <v-switch
                v-model="allowStatistics"
                :label="$t('cookieModal.statisticsCookies')"
              ></v-switch>
            </v-col>
            <v-col
              dense
              cols="12"
              md="6"
              xl="8"
              class="d-block d-md-flex justify-end"
            >
              <div>
                <v-btn small class="outlined mb-2 mb-md-0" @click="rejectAll">
                  {{ $t('cookieModal.reject') }}
                </v-btn>
              </div>
              <div>
                <v-btn
                  small
                  class="outlined ml-md-2 mb-2 mb-md-0"
                  @click="acceptSelection"
                >
                  {{ $t('cookieModal.allowSelected') }}
                </v-btn>
              </div>
              <div>
                <v-btn small class="ml-md-2" @click="acceptAll">
                  {{ $t('cookieModal.allowAll') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script>
import { cookieMixin } from '@/mixins/cookie.js';
import { mapState } from 'vuex';
import TrackingService from '@/services/TrackingService.js';

export default {
  mixins: [cookieMixin],

  data() {
    return {
      cookieName: 'cookie_prefs',
      usercentricsEventNames: [
        'ucEvent',
        'UC_SDK_EVENT',
        'UC_CONSENT',
        'UC_UI_CMP_EVENT'
      ],
      displayBanner: false,
      allowRequired: true,
      allowStatistics: false
    };
  },

  computed: {
    ...mapState('companyModule', {
      customCookieBanner: 'customCookieBanner',
      useGoogleAnalytics: 'useGoogleAnalytics',
      useMatomo: 'useMatomo',
      useUsercentrics: 'useUsercentrics',
      googleAnalyticsId: 'googleAnalyticsId',
      matomoUrl: 'matomoUrl',
      matomoSiteId: 'matomoSiteId',
      usercentricsId: 'usercentricsId',
      customDataPrivacyUrl: 'customDataPrivacyUrl',
      customAnalyticsScript: 'customAnalyticsScript',
      customAnalyticsAcceptScript: 'customAnalyticsAcceptScript',
      customAnalyticsRejectScript: 'customAnalyticsRejectScript'
    }),

    usesOwnCookieBanner() {
      return !this.customCookieBanner;
    },

    showBanner() {
      return this.displayBanner && this.usesOwnCookieBanner;
    }
  },

  watch: {
    displayBanner() {
      this.$nextTick(() => {
        this.$refs.sheet.showScroll();
      });
    }
  },

  async mounted() {
    // Arm page-event buffering up front so the first hit can be resolved by the
    // selected consent bridge.
    if (!this.getCookie(this.cookieName)) {
      TrackingService.armConsentBuffering();
    }

    if (!this.usesOwnCookieBanner) {
      this.registerUsercentricsConsentEvents();
    }

    await this.initializeAnalytics();
    const cookie = this.getCookie(this.cookieName);
    this.displayBanner = !cookie;

    if (cookie) {
      const cookieValue = JSON.parse(cookie);
      if (cookieValue.consent === 'all') {
        this.sendPageview();
      }
    }
  },

  beforeDestroy() {
    if (!this.usesOwnCookieBanner) {
      this.unregisterUsercentricsConsentEvents();
    }
  },

  methods: {
    registerUsercentricsConsentEvents() {
      this.usercentricsEventNames.forEach((eventName) => {
        window.addEventListener(eventName, this.handleUsercentricsConsent);
      });
    },

    unregisterUsercentricsConsentEvents() {
      this.usercentricsEventNames.forEach((eventName) => {
        window.removeEventListener(eventName, this.handleUsercentricsConsent);
      });
    },

    rejectAll() {
      this.setConsent('required');
      this.displayBanner = false;
      // No statistics consent: flush buffered page events anonymously (unlinked).
      // UTM is not personal data, so it is kept regardless.
      TrackingService.flushPendingEvents(false);
      this.sendRejection();
    },

    acceptSelection() {
      if (this.allowStatistics) {
        this.acceptAll();
      } else {
        this.rejectAll();
      }
    },

    acceptAll() {
      this.setConsent('all');
      this.displayBanner = false;
      // Statistics consent granted: flush buffered page events WITH the freshly
      // minted token so the first hit is linked to the session.
      TrackingService.flushPendingEvents(true);
      this.sendPageview();
    },

    setConsent(mode) {
      const cookieValue = {
        date: new Date(),
        consent: mode
      };

      this.setCookie(this.cookieName, JSON.stringify(cookieValue), 30);
    },

    async initializeAnalytics() {
      if (this.customAnalyticsScript) {
        this.initializeCustomAnalytics();
      }

      if (this.useGoogleAnalytics) {
        this.initializeGoogleAnalytics();
      }

      if (this.useMatomo) {
        this.initializeMatomoAnalytics();
      }

      if (this.useUsercentrics) {
        const uc = await this.initializeUsercentricsAnalytics();
        await uc.ready;
      }
    },

    handleUsercentricsConsent(event) {
      const decision = this.getUsercentricsConsentDecision(
        event.detail || event
      );
      if (decision === true) {
        this.applyUsercentricsConsent(true);
        return;
      }

      if (decision === false) {
        this.applyUsercentricsConsent(false);
      }
    },

    getUsercentricsConsentDecision(detail) {
      if (!detail) {
        return null;
      }

      if (
        detail.action === 'onAcceptAllServices' ||
        detail.type === 'ACCEPT_ALL'
      ) {
        return true;
      }

      if (
        detail.action === 'onDenyAllServices' ||
        detail.action === 'onUpdateServices' ||
        detail.action === 'onSaveServices' ||
        detail.type === 'SAVE' ||
        detail.type === 'DENY_ALL'
      ) {
        return false;
      }

      return null;
    },

    applyUsercentricsConsent(consented) {
      const cookie = this.getCookie(this.cookieName);
      const currentConsent = cookie ? JSON.parse(cookie).consent : null;
      const nextConsent = consented ? 'all' : 'required';
      if (currentConsent === nextConsent) {
        return;
      }

      if (consented) {
        this.acceptAll();
      } else {
        this.rejectAll();
      }
    },

    initializeGoogleAnalytics() {
      (function (i, s, o, g, r, a, m) {
        i['GoogleAnalyticsObject'] = r;
        (i[r] =
          i[r] ||
          function () {
            (i[r].q = i[r].q || []).push(arguments);
          }),
          (i[r].l = 1 * new Date());
        (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
        a.async = 1;
        a.src = g;
        m.parentNode.insertBefore(a, m);
      })(
        window,
        document,
        'script',
        'https://www.google-analytics.com/analytics.js',
        'ga'
      );
    },

    initializeMatomoAnalytics() {
      // Not yet
    },

    initializeUsercentricsAnalytics() {
      return new Promise((resolve, reject) => {
        if (!this.usercentricsId) return reject('No settingsId provided');
        if (document.getElementById('usercentrics-cmp'))
          return resolve(window.Usercentrics);

        const script = document.createElement('script');
        script.id = 'usercentrics-cmp';
        script.async = true;
        script.setAttribute('data-settings-id', this.usercentricsId);
        script.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
        script.onload = () => {
          const checkReady = setInterval(() => {
            if (window.Usercentrics?.ready) {
              clearInterval(checkReady);
              resolve(window.Usercentrics);
            }
          }, 100);
        };

        script.onerror = () => reject('Failed to load Usercentrics script');
        document.head.appendChild(script);
      });
    },

    initializeCustomAnalytics() {
      this.injectScript(this.customAnalyticsScript);
    },

    sendRejection() {
      if (this.customAnalyticsRejectScript) {
        this.injectScript(this.customAnalyticsRejectScript);
      }
    },

    sendPageview() {
      if (this.useGoogleAnalytics && this.googleAnalyticsId) {
        window.ga('create', this.googleAnalyticsId, 'auto');
        window.ga('set', 'anonymizeIp', true);
        window.ga('send', 'pageview');
      }

      if (this.customAnalyticsAcceptScript) {
        this.injectScript(this.customAnalyticsAcceptScript);
      }
    },

    injectScript(content) {
      let newScript = document.createElement('script');
      newScript.innerHTML = content;
      if (content.startsWith('<script')) {
        newScript = newScript.firstChild;
      }

      document.head.appendChild(newScript);
    }
  }
};
</script>
